/**
 * Beginn des sichtbaren Fensters auf dem Wald-Kopf: ‹ Zeitraum ▾ ›. Der Kalender wählt den ersten
 * Tag; die Länge kommt aus 7/14/30/60 daneben. Pfeile springen um die gezeigte Länge.
 */
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { de } from 'date-fns/locale/de';
import { forestArrow, forestGroup } from '@/shared/components';
import { DayPickerChevron, dayPickerRangeClassNames } from '@/shared/components/dayPickerTheme';
import { cn } from '@/shared/lib/cn';
import { rangeChipLabel, windowLabel } from './calendar/calendarFormat';

interface Props {
  from: string;
  to: string;
  onFromChange: (iso: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}

const toLocal = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, (month ?? 1) - 1, day ?? 1);
};
const toIso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function AvailabilityStartDate({ from, to, onFromChange, onPrevious, onNext }: Props) {
  const [open, setOpen] = useState(false);
  // Kurz auf dem Knopf („21. Okt. – 3. Nov.“), ausgeschrieben für die Sprachausgabe.
  const label = windowLabel(from, to);
  return (
    <div className={cn(forestGroup, 'h-11')}>
      <button
        type="button"
        onClick={onPrevious}
        aria-label="Zeitraum zurück"
        className={forestArrow}
      >
        <ChevronLeft size={18} aria-hidden />
      </button>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={`Beginn wählen: ${label}`}
            className="flex h-11 items-center gap-1.5 whitespace-nowrap px-1.5 font-mono text-xs font-semibold text-white"
          >
            {rangeChipLabel(from, to)}
            <ChevronDown size={14} aria-hidden />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={8}
            collisionPadding={16}
            aria-label="Beginn wählen"
            className="bg-card border-hairline shadow-panel rounded-tile z-50 max-h-[85dvh] overflow-y-auto border p-4"
          >
            <DayPicker
              mode="single"
              locale={de}
              weekStartsOn={1}
              defaultMonth={toLocal(from)}
              selected={toLocal(from)}
              onSelect={(date) => {
                if (!date) return;
                onFromChange(toIso(date));
                setOpen(false);
              }}
              classNames={dayPickerRangeClassNames}
              components={{ Chevron: DayPickerChevron }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <button type="button" onClick={onNext} aria-label="Zeitraum weiter" className={forestArrow}>
        <ChevronRight size={18} aria-hidden />
      </button>
    </div>
  );
}
